import {
  useEffect,
  useState,
  useRef,
  useCallback,
  useLayoutEffect,
} from "react";
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
} from "react-router-dom";
import Cookies from "js-cookie";
import axios from "axios";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Login
import ConfidentialityAgreement from "./containers/login/ConfidentialityAgreement";
import ForgotPassword from "./containers/login/ForgotPassword";
import Login from "./containers/login/Login";
import ResetPasswordByMail from "./containers/login/ResetPasswordByMail";
import ThirdPartyApi from "./containers/login/ThirdPartyApi";

// Pages
import Activity from "./containers/activity/Activity";
import ActivityCollectionDetailView from "./containers/activity/ActivityCollectionDetailView";
import ActivityRecommendationsGuide from "./containers/activity/ActivityRecommendationsGuide";
import Address from "./containers/address/Address";
import Camera from "./containers/camera/Camera";
import ChartCamera from "./containers/camera/ChartCamera";
import Container from "./containers/container/Container";
import ContainerDetailsView from "./containers/container/ContainerDetailsView";
import Dashboard from "./containers/dashboard/Dashboard";
import DigitalTwin from "./containers/digitalTwin/DigitalTwin";
import Help from "./containers/help/Help";
import SettingsHub from "./containers/settings/SettingsHub";
import SettingsSites from "./containers/settings/pages/SettingsSites";
import SettingsClients from "./containers/settings/pages/SettingsClients";
import SettingsWaste from "./containers/settings/pages/SettingsWaste";
import SettingsContainers from "./containers/settings/pages/SettingsContainers";
import SettingsCameras from "./containers/settings/pages/SettingsCameras";
import SettingsContacts from "./containers/settings/pages/SettingsContacts";
import SettingsConstants from "./containers/settings/pages/SettingsConstants";
import SettingsApi from "./containers/settings/pages/SettingsApi";
import Statistics from "./containers/statistics/Statistics";
import UserAccess from "./containers/userAccess/UserAccess";
import WasteLegacyRedirect from "./containers/settings/waste/WasteLegacyRedirect";

// App
import AppHome from "./mobileapp/AppHome";
import AppInstallation from "./mobileapp/AppInstallation";
import AppInstallationDetails from "./mobileapp/AppInstallationDetails";
import AppInstallationRotation from "./mobileapp/AppInstallationRotation";
import AppInstallationSideHub from "./mobileapp/AppInstallationSideHub";
import AppInstallationSideMask from "./mobileapp/AppInstallationSideMask";
import AppInstallationBrightness from "./mobileapp/AppInstallationBrightness";
import AppInstallationVisualization from "./mobileapp/AppInstallationVisualization";
import AppInstallationBatteryDetail from "./mobileapp/AppInstallationBatteryDetail";
import AppInstallationNetworkDetail from "./mobileapp/AppInstallationNetworkDetail";
import AppInstallationWifiConfig from "./mobileapp/AppInstallationWifiConfig";
import AppInstallationScheduleDetail from "./mobileapp/AppInstallationScheduleDetail";
import AppNotifications from "./mobileapp/AppNotifications";
import AppNotificationsDetails from "./mobileapp/AppNotificationsDetails";
import AppSettings from "./mobileapp/AppSettings";
import AppHelp from "./mobileapp/AppHelp";

// Utils
import Header from "./components/Header";
import AppSidebar from "./components/navigation/AppSidebar";
import FloatingToggleAdminView from "./components/FloatingToggleAdminView";

// Styles
import "./App.css";
import "./static/styles/styles.scss";

// Maps
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/leaflet.markercluster.js";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";

import { fetchUserLanguage } from "./redux/languageSlice";
import { useDispatch } from "react-redux";
import MaskLicenseAlert from "./components/MaskLicenseAlert";
import LoginRedirectScreen from "./containers/login/LoginRedirectScreen";
import MicrosoftSsoRedirectHandler from "./containers/login/MicrosoftSsoRedirectHandler";
import {
  reassociatePushTokenOnLogin,
  requestPushNotificationIfNeeded,
} from "./containers/login/loginSession";
import {
  hasPendingMicrosoftSsoRedirect,
  resetMicrosoftSsoRedirectState,
} from "./containers/login/microsoftSsoRedirect";
import { getCorporationData } from "./services/corporation";
import { getUserData } from "./services/users";
import { syncSessionUserNotificationPrefs } from "./shared/utils/pushHealth";
import { checkIfMobile } from "./components/mobileHelpers";
import Campaign from "./containers/campaign/Campaign";
import CampaignCreate from "./containers/campaign/CampaignCreate";
import CampaignList from "./containers/campaign/CampaignList";
import AppCameraCapture from "./mobileapp/AppCameraCapture";
import AppCollect from "./mobileapp/collection/AppCollect";
import Document from "./containers/document/Document";
import { setupForegroundPushListener } from "./firebase";
import {
  acknowledgePushEvent,
  flushPendingPushAcks,
  reportPushDiagnostics,
} from "./services/pushDiagnostics";
import ErrorBoundary from "./components/ErrorBoundary";
import UpdateBanner from "./components/UpdateBanner";
import { AppZoomProvider, useAppZoom } from "./contexts/AppZoomContext";
import Characterization from "./containers/characterization-2/characterizationArea/Characterization";
import CharacterizationList from "./containers/characterization-2/characterizationList/CharacterizationList";
import CharacterisationDetails from "./containers/characterization-2/characterizationDetails/CharacterisationDetails";
import UiKit from "./containers/uiKit/UiKit";
import ConfrontationList from "./containers/confrontation/ConfrontationList";
import ConfrontationDetail from "./containers/confrontation/ConfrontationDetail";

const isSameSerializedValue = (left, right) => {
  try {
    return JSON.stringify(left) === JSON.stringify(right);
  } catch {
    return false;
  }
};

const AppBodyZoom = () => {
  const { zoomLevel } = useAppZoom();
  useLayoutEffect(() => {
    const rootEl = document.getElementById("root");
    if (!rootEl) return;
    const zoomValue = zoomLevel === 100 ? "" : String(zoomLevel / 100);
    rootEl.style.zoom = zoomValue;
    rootEl.style.setProperty("--app-zoom", String(zoomLevel / 100));

    let styleTag = document.getElementById("app-zoom-portal-style");
    if (zoomLevel === 100) {
      if (styleTag) styleTag.remove();
    } else {
      if (!styleTag) {
        styleTag = document.createElement("style");
        styleTag.id = "app-zoom-portal-style";
        document.head.appendChild(styleTag);
      }
      styleTag.textContent = [
        `.modal-dialog { zoom: ${zoomValue}; }`,
        `.card-block { max-height: calc(100vh / var(--app-zoom, 1)); }`,
      ].join("\n");
    }

    return () => {
      rootEl.style.zoom = "";
      rootEl.style.removeProperty("--app-zoom");
      document.getElementById("app-zoom-portal-style")?.remove();
    };
  }, [zoomLevel]);
  return null;
};

const App = () => {
  const API = process.env.REACT_APP_API_BACKEND;
  const dispatch = useDispatch();
  const [token, setToken] = useState(Cookies.get("userToken") || null);
  const [user, setUser] = useState(() => {
    try {
      const storedUser = localStorage.getItem("user");
      return storedUser ? JSON.parse(storedUser) : null;
    } catch (error) {
      console.error("Error parsing user data from localStorage:", error);
      return null;
    }
  });
  const [licenseExpired, setLicenseExpired] = useState(false);
  const [corporation, setCorporation] = useState(null);
  const [, setCorporationModules] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [microsoftSsoRedirectPending, setMicrosoftSsoRedirectPending] =
    useState(() => hasPendingMicrosoftSsoRedirect());
  const userId = user?._id;
  const userRef = useRef(user);
  userRef.current = user;

  const fetchCorporationModules = useCallback(() => {
    const currentUser = userRef.current;
    if (!currentUser) return;

    getCorporationData(currentUser).then((response) => {
      if (response.status !== 200) return;
      const next = response.data.corporation;
      setCorporationModules((prev) => {
        if (isSameSerializedValue(prev, next)) return prev;
        try {
          localStorage.setItem("corporationModules", JSON.stringify(next));
        } catch {
          /* ignore quota */
        }
        return next;
      });
    });
  }, []);

  const fetchFormattedMaterial = useCallback(() => {
    axios
      .get(`${API}material`, {
        headers: { authorization: "Bearer " + token },
      })
      .then((responseMaterialsDescription) => {
        if (
          responseMaterialsDescription.status >= 200 &&
          responseMaterialsDescription.status < 400
        ) {
          localStorage.setItem(
            "materialsDescription",
            JSON.stringify(responseMaterialsDescription.data.materialList),
          );
        }
      });
  }, [API, token]);

  useEffect(() => {
    if (token && userId) {
      // Always refresh corporation flags (modules) so backoffice changes apply after reload
      fetchCorporationModules();
      fetchFormattedMaterial();
    }
  }, [token, userId, fetchCorporationModules, fetchFormattedMaterial]);

  // PWA: refresh module flags when the app comes back to foreground (no hard refresh needed).
  // Do not listen to window `focus`: it fires on every desktop tab/window switch and remounts forms.
  useEffect(() => {
    if (!token) {
      return undefined;
    }

    const refreshModulesIfVisible = () => {
      if (document.visibilityState === "visible") {
        fetchCorporationModules();
      }
    };

    const onPageShow = (event) => {
      // bfcache restore on iOS/Android PWA
      if (event.persisted) {
        fetchCorporationModules();
      }
    };

    document.addEventListener("visibilitychange", refreshModulesIfVisible);
    window.addEventListener("pageshow", onPageShow);

    return () => {
      document.removeEventListener("visibilitychange", refreshModulesIfVisible);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [token, fetchCorporationModules]);

  useEffect(() => {
    setIsMobile(checkIfMobile());

    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);

    return () => {
      window.removeEventListener("resize", checkIfMobile);
    };
  }, []);

  const isAkanthasAccount = (Cookies.get("userEmail") || "").includes(
    "@akanthas.com",
  );

  const [viewMode, setViewMode] = useState(Cookies.get("viewMode") || "client");

  /** Cookie + état App + événement global pour que les hooks / memo ne dépendent pas d’un rechargement. */
  const applyViewMode = useCallback((mode) => {
    const next = mode === "admin" ? "admin" : "client";
    Cookies.set("viewMode", next, {
      secure: true,
      expires: 90,
      sameSite: "Lax",
    });
    setViewMode(next);
    window.dispatchEvent(
      new CustomEvent("viewModeChanged", { detail: { viewMode: next } }),
    );
  }, []);

  const cookieSideNavExpandedValue = Cookies.get("sideNavExpanded");
  const parsedSideNavExpandedValue = cookieSideNavExpandedValue
    ? JSON.parse(cookieSideNavExpandedValue)
    : true;
  const appContainerRef = useRef(null);
  const sideNavExpandedRef = useRef(parsedSideNavExpandedValue);

  // Initialiser la classe CSS au montage et quand token/user deviennent disponibles
  // Utiliser useLayoutEffect pour s'assurer que la classe est appliquée avant le paint
  useLayoutEffect(() => {
    if (appContainerRef.current && token && userId) {
      // Retirer les classes existantes pour éviter les doublons
      appContainerRef.current.classList.remove("nav-expanded", "nav-collapsed");
      // Ajouter la classe appropriée
      appContainerRef.current.classList.add(
        sideNavExpandedRef.current ? "nav-expanded" : "nav-collapsed",
      );
    }
  }, [token, userId]);

  // Fonction pour mettre à jour l'état de la navigation sans causer de re-render
  const updateNavExpanded = useCallback((expanded) => {
    sideNavExpandedRef.current = expanded;
    if (appContainerRef.current) {
      appContainerRef.current.classList.remove("nav-expanded", "nav-collapsed");
      appContainerRef.current.classList.add(
        expanded ? "nav-expanded" : "nav-collapsed",
      );
    }
  }, []);

  const fcmToken = Cookies.get("fcmToken");

  const setCookies = useCallback(
    (tokenToSet, firstName, userEmail) => {
      if (tokenToSet) {
        applyViewMode("client");
        // Persist across PWA/process kills (session cookies were cleared by Android).
        const authCookieOpts = { secure: true, expires: 90, sameSite: "Lax" };
        Cookies.set("userToken", tokenToSet, authCookieOpts);
        Cookies.set("firstName", firstName, authCookieOpts);
        Cookies.set("userEmail", userEmail, authCookieOpts);
        setToken(tokenToSet);
      } else {
        resetMicrosoftSsoRedirectState();
        setViewMode("client");
        setCorporationModules(null);
        localStorage.removeItem("corporationModules");
        Cookies.remove("viewMode");
        Cookies.remove("userToken");
        Cookies.remove("firstName");
        Cookies.remove("userEmail");
        Cookies.remove("fcmToken");
        setToken(null);
        window.dispatchEvent(
          new CustomEvent("viewModeChanged", {
            detail: { viewMode: "client" },
          }),
        );
      }
    },
    [applyViewMode],
  );

  const saveUser = useCallback(async (userToSave) => {
    if (userToSave) {
      localStorage.setItem("user", JSON.stringify(userToSave));
      setUser(userToSave);
      const { clearChar2AlertsShown } = await import(
        "./containers/characterization-2/core/utils/alertOncePerSession"
      );
      clearChar2AlertsShown();
    } else {
      localStorage.removeItem("user");
      setUser(null);
    }
  }, []);

  const completeLogin = useCallback(
    async (userData) => {
      const previousUserId = (() => {
        try {
          return JSON.parse(localStorage.getItem("user") || "null")?._id;
        } catch {
          return null;
        }
      })();
      setCookies(userData.token, userData.firstName, userData.email);
      await saveUser(userData);
      await reassociatePushTokenOnLogin(userData, previousUserId);
    },
    [setCookies, saveUser],
  );

  useEffect(() => {
    if (userId) {
      dispatch(fetchUserLanguage(userId));
    }
  }, [userId, dispatch]);

  useEffect(() => {
    if (!token || !userId) return undefined;

    getCorporationData(userRef.current).then((responseCorporation) => {
      if (
        responseCorporation.status >= 200 &&
        responseCorporation.status < 400
      ) {
        const nextCorporation = responseCorporation.data.corporation;
        const licenseIsExpired = nextCorporation?.licenseExpired || false;
        setLicenseExpired(licenseIsExpired);
        setCorporation((prev) =>
          isSameSerializedValue(prev, nextCorporation) ? prev : nextCorporation,
        );
        // Only lock scrolling on desktop where we show the blocking mask.
        // On mobile, locking body scroll makes screens appear "frozen".
        if (
          licenseIsExpired &&
          !(isAkanthasAccount && viewMode === "admin") &&
          !isMobile
        ) {
          document.body.style.overflow = "hidden";
        } else {
          document.body.style.overflow = "";
        }
      }
    });
    return undefined;
  }, [userId, token, setLicenseExpired, isAkanthasAccount, viewMode, isMobile]);

  /** Keep session user prefs in sync with API after refresh / tab focus. */
  const refreshSessionUser = useCallback(async () => {
    if (!token) return;

    let current;
    try {
      current = JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return;
    }
    if (!current?._id) return;

    try {
      const response = await getUserData(current._id);
      const fresh = response?.data?.user;
      if (!fresh) return;

      const syncedPrefs = syncSessionUserNotificationPrefs(fresh);
      const base = syncedPrefs || current;
      const next = {
        ...base,
        firstName: fresh.firstName ?? base.firstName,
        lastName: fresh.lastName ?? base.lastName,
        email: fresh.email ?? base.email,
        phone: fresh.phone ?? base.phone,
        language: fresh.language ?? base.language,
        authorizedAddresses:
          fresh.authorizedAddresses ?? base.authorizedAddresses,
        token: current.token || fresh.token || base.token,
      };

      if (isSameSerializedValue(current, next)) {
        return;
      }

      localStorage.setItem("user", JSON.stringify(next));
      setUser(next);
      window.dispatchEvent(
        new CustomEvent("sessionUserNotificationsUpdated", {
          detail: { user: next },
        }),
      );
    } catch (error) {
      console.error("Failed to refresh session user:", error);
    }
  }, [token]);

  useEffect(() => {
    if (!token) return undefined;
    void refreshSessionUser();
    return undefined;
  }, [token, refreshSessionUser]);

  useEffect(() => {
    if (!token) return undefined;

    const refreshIfVisible = () => {
      if (document.visibilityState === "visible") {
        void refreshSessionUser();
        reportPushDiagnostics();
        flushPendingPushAcks();
      }
    };

    const onPageShow = (event) => {
      if (event.persisted) {
        void refreshSessionUser();
      }
    };

    document.addEventListener("visibilitychange", refreshIfVisible);
    window.addEventListener("pageshow", onPageShow);

    return () => {
      document.removeEventListener("visibilitychange", refreshIfVisible);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [token, refreshSessionUser]);

  useEffect(() => {
    const handleSessionNotificationsUpdated = (event) => {
      const nextUser = event?.detail?.user;
      if (!nextUser) return;
      setUser((prev) => {
        if (!prev || String(prev._id) !== String(nextUser._id)) {
          return prev;
        }
        const merged = {
          ...prev,
          ...nextUser,
          token: prev.token || nextUser.token,
        };
        return isSameSerializedValue(prev, merged) ? prev : merged;
      });
    };
    window.addEventListener(
      "sessionUserNotificationsUpdated",
      handleSessionNotificationsUpdated,
    );
    return () => {
      window.removeEventListener(
        "sessionUserNotificationsUpdated",
        handleSessionNotificationsUpdated,
      );
    };
  }, []);

  useEffect(() => {
    const syncPushToken = async () => {
      if (!isMobile || !user) return;
      await requestPushNotificationIfNeeded(user);
      await reportPushDiagnostics();
    };
    void syncPushToken();
  }, [user, isMobile, fcmToken]);

  useEffect(() => {
    if (!token || !userId) return undefined;

    reportPushDiagnostics();
    flushPendingPushAcks();
    setupForegroundPushListener();

    const onServiceWorkerMessage = (event) => {
      if (event?.data?.type === "notification" && event.data.payload) {
        acknowledgePushEvent(event.data.payload, "delivered");
      }
    };
    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener(
        "message",
        onServiceWorkerMessage,
      );
    }

    return () => {
      if (navigator.serviceWorker) {
        navigator.serviceWorker.removeEventListener(
          "message",
          onServiceWorkerMessage,
        );
      }
    };
  }, [token, userId]);

  return (
    <Router>
      <MicrosoftSsoRedirectHandler
        api={API}
        completeLogin={completeLogin}
        onRedirectFinished={() => setMicrosoftSsoRedirectPending(false)}
      />
      <main>
        <UpdateBanner />
        {token && !isMobile && user && (
          <AppZoomProvider>
            <>
              <AppBodyZoom />
              <AppSidebar
                setSideNavExpanded={updateNavExpanded}
                viewMode={viewMode}
                corporation={corporation}
                user={user}
                setCookies={setCookies}
                saveUser={saveUser}
              />

              {isAkanthasAccount && (
                <FloatingToggleAdminView
                  viewMode={viewMode}
                  setViewMode={applyViewMode}
                />
              )}

              <ToastContainer
                position="top-right"
                marginTop={"60px"}
                autoClose={2500}
                pauseOnFocusLoss={false}
                newestOnTop
                style={{ zIndex: 99999 }}
              />

              <div ref={appContainerRef} className="app-container">
                <Header
                  saveUser={saveUser}
                  setCookies={setCookies}
                  userData={user}
                  licenseExpired={licenseExpired}
                  corporation={corporation}
                />

                <ErrorBoundary key={window.location.pathname}>
                  <Switch>
                    <Route path="/access">
                      <UserAccess
                        corporation={corporation}
                        viewMode={viewMode}
                      />
                    </Route>

                    <Route exact path="/activity/detail">
                      <ActivityCollectionDetailView />
                    </Route>

                    <Route exact path="/activity/recommendations-guide">
                      <ActivityRecommendationsGuide />
                    </Route>

                    <Route exact path="/activity">
                      <Activity />
                    </Route>

                    <Route path="/address">
                      <Address token={token} API={API} />
                    </Route>

                    <Route path="/cameras">
                      <Camera />
                    </Route>

                    <Route path="/camera/:id">
                      <ChartCamera token={token} API={API} />
                    </Route>

                    <Route path="/containers">
                      <Container />
                    </Route>

                    <Route path="/container/:id">
                      <ContainerDetailsView />
                    </Route>

                    <Route path="/characterizations">
                      <Redirect to="/characterization-areas" />
                    </Route>

                    <Route path="/characterization-areas">
                      <Characterization />
                    </Route>

                    <Route exact path="/characterization/list">
                      <CharacterizationList />
                    </Route>

                    <Route path="/characterization/list/:id">
                      <CharacterizationList />
                    </Route>

                    <Route path="/characterization/details/:id">
                      <CharacterisationDetails />
                    </Route>

                    <Route path="/digitaltwins">
                      <DigitalTwin />
                    </Route>

                    <Route path="/help">
                      <Help />
                    </Route>

                    <Route path="/statistics">
                      <Statistics token={token} />
                    </Route>

                    <Route exact path="/settings">
                      <SettingsHub />
                    </Route>

                    <Route path="/settings/sites">
                      <SettingsSites token={token} API={API} />
                    </Route>

                    <Route path="/settings/clients">
                      <SettingsClients token={token} API={API} />
                    </Route>

                    <Route path="/settings/waste">
                      <SettingsWaste token={token} API={API} />
                    </Route>

                    <Route path="/settings/containers">
                      <SettingsContainers token={token} API={API} />
                    </Route>

                    <Route path="/settings/cameras">
                      <SettingsCameras />
                    </Route>

                    <Route path="/settings/contacts">
                      <SettingsContacts token={token} API={API} />
                    </Route>

                    <Route path="/settings/constants">
                      <SettingsConstants token={token} API={API} />
                    </Route>
                    <Route exact path="/settings/api">
                      <Redirect to="/api" />
                    </Route>
                    <Route path="/api">
                      <SettingsApi />
                    </Route>

                    <Route path="/waste">
                      <WasteLegacyRedirect />
                    </Route>

                    <Route exact path="/campaign/create">
                      <CampaignCreate />
                    </Route>
                    <Route exact path="/campaign/:id/edit">
                      <CampaignCreate />
                    </Route>
                    <Route exact path="/campaign">
                      <CampaignList />
                    </Route>
                    <Route path="/campaign/:id">
                      <Campaign />
                    </Route>

                    <Route exact path="/confrontation">
                      <ConfrontationList />
                    </Route>
                    <Route path="/confrontation/:id">
                      <ConfrontationDetail />
                    </Route>

                    <Route path="/documents">
                      <Document />
                    </Route>

                    <Route path="/ui-kit">
                      <UiKit />
                    </Route>

                    <Route path="/">
                      <Dashboard
                        user={user}
                        saveUser={saveUser}
                        corporation={corporation}
                      />
                    </Route>
                  </Switch>
                </ErrorBoundary>
                <div
                  className={
                    licenseExpired &&
                    !(isAkanthasAccount && viewMode === "admin")
                      ? ""
                      : "hidden"
                  }
                  aria-hidden={
                    !(
                      licenseExpired &&
                      !(isAkanthasAccount && viewMode === "admin")
                    )
                  }
                >
                  <MaskLicenseAlert />
                </div>
              </div>
            </>
          </AppZoomProvider>
        )}

        {(!token || !user) && (
          <>
            {microsoftSsoRedirectPending ? (
              <LoginRedirectScreen />
            ) : (
              <Switch>
                <Route path="/password/updatebymail/:token">
                  <ResetPasswordByMail API={API} />
                </Route>

                <Route path="/login">
                  <Login completeLogin={completeLogin} API={API} />
                </Route>

                <Route path="/confidentialityagreement">
                  <ConfidentialityAgreement />
                </Route>

                <Route path="/third-party-api">
                  <ThirdPartyApi />
                </Route>

                <Route path="/forgotpassword">
                  <ForgotPassword API={API} />
                </Route>

                <Route path="*">
                  <Redirect to="/login" API={API} />
                </Route>
              </Switch>
            )}
          </>
        )}

        {token && user && isMobile && (
          <>
            <Switch>
              <Route path="/app/home">
                <AppHome setCookies={setCookies} saveUser={saveUser} />
              </Route>

              <Route path="/app/installation">
                <AppInstallation setCookies={setCookies} saveUser={saveUser} />
              </Route>

              <Route path="/app/installationdetails/:id/visualization/brightness">
                <AppInstallationBrightness
                  setCookies={setCookies}
                  saveUser={saveUser}
                />
              </Route>

              <Route path="/app/installationdetails/:id/visualization/:side/rotation">
                <AppInstallationRotation
                  setCookies={setCookies}
                  saveUser={saveUser}
                />
              </Route>

              <Route path="/app/installationdetails/:id/visualization/:side/mask">
                <AppInstallationSideMask
                  setCookies={setCookies}
                  saveUser={saveUser}
                />
              </Route>

              <Route path="/app/installationdetails/:id/visualization/:side">
                <AppInstallationSideHub
                  setCookies={setCookies}
                  saveUser={saveUser}
                />
              </Route>

              <Route exact path="/app/installationdetails/:id/visualization">
                <AppInstallationVisualization
                  setCookies={setCookies}
                  saveUser={saveUser}
                />
              </Route>

              <Route path="/app/installationdetails/:id/state/battery">
                <AppInstallationBatteryDetail
                  setCookies={setCookies}
                  saveUser={saveUser}
                />
              </Route>

              <Route path="/app/installationdetails/:id/state/wifi">
                <AppInstallationWifiConfig
                  setCookies={setCookies}
                  saveUser={saveUser}
                />
              </Route>

              <Route path="/app/installationdetails/:id/state/cellular">
                <AppInstallationNetworkDetail
                  setCookies={setCookies}
                  saveUser={saveUser}
                />
              </Route>

              <Route
                exact
                path="/app/installationdetails/:id/state/network/wifi"
                render={({ match }) => (
                  <Redirect
                    to={`/app/installationdetails/${match.params.id}/state/wifi`}
                  />
                )}
              />

              <Route
                exact
                path="/app/installationdetails/:id/state/network"
                render={({ match }) => (
                  <Redirect
                    to={`/app/installationdetails/${match.params.id}/state/cellular`}
                  />
                )}
              />

              <Route path="/app/installationdetails/:id/schedule">
                <AppInstallationScheduleDetail
                  setCookies={setCookies}
                  saveUser={saveUser}
                />
              </Route>

              <Route path="/app/installationdetails/:id">
                <AppInstallationDetails
                  setCookies={setCookies}
                  saveUser={saveUser}
                />
              </Route>

              <Route path="/app/notifications">
                <AppNotifications setCookies={setCookies} saveUser={saveUser} />
              </Route>

              <Route path="/app/notificationsdetails">
                <AppNotificationsDetails
                  setCookies={setCookies}
                  saveUser={saveUser}
                />
              </Route>

              <Route path="/app/collect">
                <AppCollect setCookies={setCookies} saveUser={saveUser} />
              </Route>

              <Route path="/app/photo">
                <AppCameraCapture setCookies={setCookies} saveUser={saveUser} />
              </Route>

              <Route path="/app/settings">
                <AppSettings setCookies={setCookies} saveUser={saveUser} />
              </Route>

              <Route path="/app/help">
                <AppHelp setCookies={setCookies} saveUser={saveUser} />
              </Route>

              <Route path="*">
                <Redirect to="/app/home" />
              </Route>
            </Switch>
          </>
        )}
      </main>
    </Router>
  );
};

export default App;
